import { twMerge } from 'tailwind-merge';
import { useNewCase } from '@/providers/NewCaseProvider';
import Dialog, { DialogBody, DialogTitle } from '@/components/Dialog';
import LoopingEllipsis from '@/components/LoopingEllipsis';
import Card from '@/components/Card';
import StepStatus from './StepStatus';
import SubmissionRow from './SubmissionRow';
import TaskRow from './TaskRow';
import type { CreationStep, CreationStepStatus } from '../types';
import { CREATION_STEP_STATUS } from '../constants';
import {
  classes,
  getFileStatus,
  getProjectStatus,
  getServiceStatus,
  getSubmissionStatus,
  getTaskStatus,
} from './utils';

const { PENDING } = CREATION_STEP_STATUS;

export type CreationProgressDialogProps = {
  step: CreationStep;
  submissionStatuses: Record<string, CreationStepStatus>;
  taskStatuses: Record<string, CreationStepStatus>;
  onRetrySubmission: (uuid: string) => void;
  onRetryTask: (uuid: string) => void;
};

export default function CreationProgressDialog({
  step,
  submissionStatuses,
  taskStatuses,
  onRetrySubmission,
  onRetryTask,
}: CreationProgressDialogProps) {
  const { selectedFiles, pendingSubmissions, pendingTasks, serviceType } =
    useNewCase();

  const renderProjectStatus = () => {
    const status = getProjectStatus(step);
    return (
      <Card className={classes.card}>
        <StepStatus status={status} />
        Creating case
      </Card>
    );
  };

  const renderFiles = () => {
    return Object.values(selectedFiles)
      .flat()
      .map((f) => {
        const status = getFileStatus(step, f);
        return (
          <Card
            key={f.uuid}
            className={twMerge(
              classes.card,
              status === PENDING && classes.pending
            )}>
            <StepStatus status={getFileStatus(step, f)} />
            {f.file.name}
          </Card>
        );
      });
  };

  const renderServiceStatus = () => {
    if (!serviceType) return null;
    const status = getServiceStatus(step);
    return (
      <Card
        className={twMerge(
          classes.card,
          status === PENDING && classes.pending
        )}>
        <StepStatus status={status} />
        Initializing service
      </Card>
    );
  };

  const renderSubmissions = () => {
    return pendingSubmissions.map((s) => (
      <SubmissionRow
        key={s.uuid}
        submission={s}
        status={getSubmissionStatus(s.uuid, submissionStatuses)}
        onRetry={() => onRetrySubmission(s.uuid)}
      />
    ));
  };

  const renderTasks = () => {
    return pendingTasks.map((t) => (
      <TaskRow
        key={t.uuid}
        task={t}
        status={getTaskStatus(t.uuid, taskStatuses)}
        onRetry={() => onRetryTask(t.uuid)}
      />
    ));
  };

  return (
    <Dialog
      disableEscapeKeyDown
      className={classes.dialog}
      onClose={() => null}>
      <DialogTitle
        className={classes.dialogTitle}
        title={
          <span>
            Saving
            <LoopingEllipsis />
          </span>
        }
      />
      <DialogBody className={classes.body}>
        {renderProjectStatus()}
        {renderFiles()}
        {renderSubmissions()}
        {renderTasks()}
        {renderServiceStatus()}
      </DialogBody>
    </Dialog>
  );
}
